import { useDataTablePagination } from "@/hooks/use-data-table-pagination";
import { useDataTableSorting } from "@/hooks/use-data-table-sorting";
import {
  DEFAULT_LANGUAGE,
  type SupportedLanguage,
} from "@/i18n/types/supported-language.types";
import { useSelectedControlStore } from "@/shared/stores/selected-control.store";
import {
  useGetControlResult,
  useGetControlResultSourceListPaginated,
} from "@/spz-api/control-results/control-results";
import type {
  ControlResulSourceColumnResponseDto,
  ControlResultCurrencyRelationshipDto,
  ControlResultListItemResponseDto,
  ControlResultSourceListItemResponseDto,
  GetControlResultDetailsItemDtoData,
} from "@/spz-api/model";
import { SortDirection } from "@spz/shared-domain";
import { EmptyCell } from "@spz/ui/components/table/cells/EmptyCell";
import { AmountCell } from "@spz/ui/components/table/cells/amount-cell";
import { DateCell } from "@spz/ui/components/table/cells/date-cell";
import { NumericCell } from "@spz/ui/components/table/cells/numeric-cell";
import { TextCell } from "@spz/ui/components/table/cells/text-cell";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { TFunction, i18n } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "../../../i18n/i18n";
import { useWorkflowStepUpdateWorkflowTransition } from "../hooks/mutations/use-control-result-update-workflow-step.mutation";
import { mapColumnTypeToCellType } from "../utils/column-type-mapper";
import { formatCellValue } from "../utils/format-cell-value";
import { parseTransitions } from "../utils/workflow-parsers";
export function formatResultValues(
  t: TFunction,
  i18n: i18n,
  data?: GetControlResultDetailsItemDtoData,
  associatedCurrency?: ControlResultCurrencyRelationshipDto | null,
  controlResultColumns?: { type: string; columnKey: string }[],
): Array<{ key: string; value: string | number }> {
  if (!data) return [];

  return Object.entries(data).map(([key, value]) => {
    const column = controlResultColumns?.find((col) => col.columnKey === key);
    const valuedFormatted = column
      ? formatCellValue(
          data,
          column.type,
          key as keyof ControlResultListItemResponseDto,
          t,
          i18n,
          associatedCurrency?.minorUnit,
          associatedCurrency?.alphabeticalCode,
        )
      : value;
    return {
      key,
      value: String(valuedFormatted),
    };
  });
}

function createSourceDataColumns(
  columns: ControlResulSourceColumnResponseDto[],
  t: (key: string) => string,
  locale: SupportedLanguage,
  associatedCurrency: ControlResultCurrencyRelationshipDto | null,
) {
  const columnHelper =
    createColumnHelper<ControlResultSourceListItemResponseDto>();

  return columns.map((col) => {
    const cellType = mapColumnTypeToCellType(col.type);
    return columnHelper.accessor((row) => row.data[col.columnKey], {
      id: col.columnKey,
      header: t(col.title),
      cell: (info) => {
        const value = info.getValue();

        switch (cellType) {
          case "date":
            return (
              <DateCell
                value={value as string | Date}
                locale={locale}
                className="cursor-text"
              />
            );
          case "numeric":
            return (
              <NumericCell value={value as number} className="cursor-text" />
            );

          case "monetary":
            return associatedCurrency ? (
              <AmountCell
                value={value as number}
                currency={associatedCurrency}
                className="cursor-text"
              />
            ) : (
              <EmptyCell className="cursor-text" />
            );
          default:
            return value == null || value === "" ? (
              <EmptyCell className="cursor-text" />
            ) : (
              <TextCell value={String(value)} className="cursor-text" />
            );
        }
      },
    });
  });
}
export const selectControlResultDetailsViewModel = ({
  controlId,
  controlResultId,
  onLoadError,
}: {
  controlId: string;
  controlResultId: string;
  onLoadError: () => void;
}) => {
  const { t, i18n } = useTranslation();

  const { pagination, setPagination } = useDataTablePagination();
  const { sorting, setSorting } = useDataTableSorting({
    onSortChange: () => {
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
      }));
    },
  });
  const [columnSizing, setColumnSizing] = useState({});

  const {
    data: resultDetails,
    isLoading,
    error,
  } = useGetControlResult(controlId, controlResultId);

  // biome-ignore lint/correctness/useExhaustiveDependencies: onLoadError no a callback
  useEffect(() => {
    if (error) {
      onLoadError();
    }
  }, [error]);

  const transitions = useMemo(
    () => parseTransitions(resultDetails?.data.workflow?.transitions),
    [resultDetails?.data.workflow?.transitions],
  );
  const { control } = useSelectedControlStore();

  // Fetch source data with pagination
  const { data: dataSource, isLoading: isLoadingSource } =
    useGetControlResultSourceListPaginated(controlId, controlResultId, {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortField: sorting[0]?.id,
      sortDirection: sorting[0]?.desc ? SortDirection.DESC : SortDirection.ASC,
    });
  const formattedResultValues = useMemo(() => {
    const currency = dataSource?.relationships?.associatedCurrency ?? null;
    return formatResultValues(
      t,
      i18n,
      resultDetails?.data.data,
      currency,
      control?.controlResultColumns,
    );
  }, [
    resultDetails?.data.data,
    dataSource?.relationships.associatedCurrency,
    control?.controlResultColumns,
    t,
    i18n,
  ]);

  // Create columns from API response
  const locale = (i18next.language as SupportedLanguage) || DEFAULT_LANGUAGE;
  const columns = useMemo(
    () =>
      createSourceDataColumns(
        dataSource?.relationships.columns ?? [],
        t,
        locale,
        dataSource?.relationships.associatedCurrency ?? null,
      ),
    [
      dataSource?.relationships.columns,
      t,
      locale,
      dataSource?.relationships.associatedCurrency,
    ],
  );

  // Create table instance
  const table = useReactTable({
    data: dataSource?.data ?? [],
    state: {
      pagination,
      sorting,
      columnSizing,
    },
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: dataSource?.total ?? 0,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    enableSortingRemoval: true,
    columnResizeMode: "onChange",
  });

  const workflowStepUpdateWorkflowTransition =
    useWorkflowStepUpdateWorkflowTransition({
      controlId,
      controlResultId,
    });

  return {
    table,
    pagination: {
      currentPage: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      totalPages: dataSource?.pageCount ?? 1,
      totalCount: dataSource?.total ?? 0,
    },
    isLoading: isLoadingSource,
    resultValues: {
      data: formattedResultValues,
      isLoading,
    },
    rootCause: resultDetails?.data.conclusion,
    owner: resultDetails?.data.owner,
    workflow: {
      ...resultDetails?.data.workflow,
      transitions,
    },
    priority: resultDetails?.data.priority,
    handleTransitionSubmit: workflowStepUpdateWorkflowTransition.mutate,
  };
};
